import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { OrderFormData, SaleType, isBidditOrPublicSale } from '@/types/order';
import { ShoppingBag, Gavel, Users, HelpCircle, AlertCircle, KeyRound } from 'lucide-react';
import { RecurringSlots } from '../components/RecurringSlots';

interface Step1ContexteProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

const typeVenteOptions = [
  {
    value: 'gre_a_gre' as SaleType,
    label: 'Gré à gré',
    description: 'Vente classique entre particuliers',
    icon: ShoppingBag,
  },
  {
    value: 'biddit' as SaleType,
    label: 'Biddit',
    description: 'Enchères en ligne notariales',
    icon: Gavel,
  },
  {
    value: 'vente_publique' as SaleType,
    label: 'Vente publique',
    description: 'Vente aux enchères traditionnelle',
    icon: Users,
  },
  {
    value: 'location' as SaleType,
    label: 'Location',
    description: 'Mise en location du bien',
    icon: KeyRound,
  },
  {
    value: 'autre' as SaleType,
    label: 'Autre',
    description: 'Autre contexte de vente',
    icon: HelpCircle,
  },
];

export function Step1Contexte({ data, onChange }: Step1ContexteProps) {
  const isBidditOrPublic = isBidditOrPublicSale(data.typeVente);
  const needsDeadline = isBidditOrPublic;
  const showDeadlineWarning = needsDeadline && !data.deadline;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
          Contexte de la vente
        </h3>
        <p className="text-lg text-navy-400">
          Quel type de vente organisez-vous ?
        </p>
      </div>

      {/* Type de vente - Grid 2x2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {typeVenteOptions.map((type) => {
          const Icon = type.icon;
          const isSelected = data.typeVente === type.value;

          return (
            <motion.button
              key={type.value}
              type="button"
              onClick={() => onChange({ typeVente: type.value })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative p-6 rounded-2xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-gold-500 bg-gold-50/50 shadow-md'
                  : 'border-navy-100 bg-white hover:border-navy-200'
              }`}
            >
              <div className="flex flex-col items-center text-center gap-3">
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    isSelected ? 'bg-gold-100' : 'bg-cream'
                  }`}
                >
                  <Icon
                    className={`w-7 h-7 ${
                      isSelected ? 'text-gold-600' : 'text-navy-400'
                    }`}
                  />
                </div>
                <div>
                  <div
                    className={`font-semibold text-lg mb-1 ${
                      isSelected ? 'text-navy-600' : 'text-navy-600'
                    }`}
                  >
                    {type.label}
                  </div>
                  <div className="text-sm text-navy-400">{type.description}</div>
                </div>
              </div>

              {isSelected && (
                <motion.div
                  layoutId="selectedSaleType"
                  className="absolute inset-0 rounded-2xl border-2 border-gold-500 pointer-events-none"
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Date de vente (si Biddit/Publique) */}
      {needsDeadline && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <label
            htmlFor="deadline"
            className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-3"
          >
            <svg
              className="w-4 h-4 text-gold-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Date de vente *
          </label>
          <Input
            id="deadline"
            type="date"
            value={data.deadline || ''}
            onChange={(e) => onChange({ deadline: e.target.value })}
            className="max-w-md"
            required={needsDeadline}
          />

          {showDeadlineWarning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                La date de vente est requise pour les ventes Biddit et publiques.
              </p>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Créneaux récurrents (si Biddit/Publique) */}
      {isBidditOrPublic && data.deadline && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <RecurringSlots
            slots={data.slotCreneaux}
            onSlotsChange={(slots) => onChange({ slotCreneaux: slots })}
            startDate={data.slotDateDebut}
            onStartDateChange={(date) => onChange({ slotDateDebut: date })}
            deadline={data.deadline}
          />
        </motion.div>
      )}

      {/* Description de la mission */}
      <div>
        <label
          htmlFor="descriptionMission"
          className="block text-sm font-medium text-navy-600 mb-2"
        >
          Description de la mission (optionnel)
        </label>
        <p className="text-sm text-navy-400 mb-3">
          Contexte particulier, contraintes, informations utiles...
        </p>
        <textarea
          id="descriptionMission"
          rows={4}
          placeholder="Ex: Vente suite à succession, bien occupé par les héritiers jusqu'au 15 mars..."
          value={data.descriptionMission || ''}
          onChange={(e) => onChange({ descriptionMission: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-gold-500 focus:outline-none focus:ring-4 focus:ring-gold-500/20 transition-all resize-none text-navy-600 placeholder:text-navy-300"
        />
      </div>
    </motion.div>
  );
}
